import type { ElementType } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useUser } from '@r/api/auth.tsx';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@r/utils/utils.ts';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';
import { switchToWorkspace, inviteWorkspace } from '@r/api/generated/client.ts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@r/components/ui/dialog.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { Button } from '@r/components/ui/button.tsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@r/components/ui/form.tsx';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@r/components/ui/dropdown-menu.tsx';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@r/components/ui/sidebar.tsx';
import { DropdownMenuSeparator } from '@radix-ui/react-dropdown-menu';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { reloadResources } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { clearAdminTablePersistence } from '@r/utils/data-table-persistence.ts';

import { ShieldUser, CircleX } from 'lucide-react';

export function WorkspaceSwitcher({
  workspaces
}: {
  workspaces: {
    active: boolean;
    name: string;
    logo: ElementType;
    plan: string;
    value: number;
    roles: string[];
  }[];
}) {
  const { isMobile } = useSidebar();
  const router = useRouter();
  const location = router.state.location;
  const user = useUser().data;
  const { updateFormData } = useWorkspaceFormStore();
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const isUserSuperAdmin =
    getActiveWorkspaceUniversal(workspaces)?.roles.includes('ROLE_FIDDY_ADMIN') ?? false;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const workspaceFormSchema = z.object({
    name: z
      .string()
      .min(2, t('form.workspace.name.too.short'))
      .max(50, t('form.workspace.name.too.long'))
      .describe(t('form.workspace.schema.describe.name')),
    domain: z
      .string()
      .regex(
        /^((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9])$/,
        t('form.workspace.invalid.domain')
      )
      .describe(t('form.workspace.schema.describe.domain'))
  });

  const form = useForm<z.infer<typeof workspaceFormSchema>>({
    resolver: zodResolver(workspaceFormSchema),
    defaultValues: {
      name: '',
      domain: ''
    }
  });

  const [activeWorkspace, setActiveWorkspace] = useState(
    getActiveWorkspaceUniversal(workspaces) ?? workspaces[0]
  );

  const previousWorkspaceRef = useRef(activeWorkspace);

  const changeWorkspaceMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      const workspaceIdInt = Number.parseInt(workspaceId, 10);
      const workspaceUniqueCode = user?.visibleWorkspaces.find(
        (workspace) => workspaceIdInt === workspace.workspace.id
      )?.uniqueCode;
      if (!workspaceUniqueCode) {
        throw new Error('Expected workspace unique code');
      }

      return await switchToWorkspace({
        workspaceId: workspaceIdInt,
        uniqueCode: workspaceUniqueCode
      });
    },
    onSuccess: async (data) => {
      clearAdminTablePersistence(user?.id);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      window.location.href = '/';
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return ['authenticated-user', 'workspaceConfigs', 'availableRoutes'].includes(
            query.queryKey[0] as string
          );
        }
      });
      await reloadResources();
      await router.invalidate();
    },
    onError: (error) => {
      showOrvalErrorToast(error, 'Failed to switch workspace');
    }
  });

  const createWorkspaceMutation = useMutation({
    mutationFn: async (values: z.infer<typeof workspaceFormSchema>) => {
      const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
      if (!activeWorkspace) throw new Error('Expected active workspace');
      const rawUniqueCode = activeWorkspace.uniqueCode;
      if (rawUniqueCode === undefined || rawUniqueCode === '') {
        throw new Error('Expected active workspace unique code');
      }
      const uniqueCode: string = rawUniqueCode;
      const userId = user?.id;
      const newWorkspace = await inviteWorkspace({
        name: values.name,
        invitedUserWorkspace: {
          user: `/admin/users/${userId}`,
          uniqueCode: uniqueCode
        },
        domain: values.domain
      });
      await updateFormData(newWorkspace.id as number, {
        name: values.name,
        domain: values.domain
      });
      return await switchToWorkspace({
        workspaceId: newWorkspace.id,
        uniqueCode
      });
    },
    onSuccess: async (data) => {
      toast.success(t('form.workspace.create.success'));
      clearAdminTablePersistence(user?.id);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      window.location.href = '/';
      form.reset();
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return ['authenticated-user', 'workspaceConfigs', 'availableRoutes'].includes(
            query.queryKey[0] as string
          );
        }
      });
      await reloadResources();
      await router.invalidate();
    },

    onError: (error) => {
      showOrvalErrorToast(error, t('form.workspace.create.error'));
    }
  });

  function onSubmit(values: z.infer<typeof workspaceFormSchema>) {
    createWorkspaceMutation.mutate(values);
  }

  useEffect(() => {
    if (previousWorkspaceRef.current !== activeWorkspace) {
      changeWorkspaceMutation.mutate(activeWorkspace.value.toString());
    }
    previousWorkspaceRef.current = activeWorkspace;
  }, [activeWorkspace]);

  const isFiddyAdminRoute = location.pathname.startsWith('/fiddy-admin');
  const adminNavigationTarget = isFiddyAdminRoute ? '/' : '/fiddy-admin';

  const handleAdminFiddyNavigation = async () => {
    try {
      await router.navigate({ to: adminNavigationTarget });
      toast.success(
        adminNavigationTarget === '/'
          ? t('admin.navigation.back.success')
          : t('admin.navigation.to.success')
      );
    } catch (error) {
      toast.error(t('admin.navigation.error'));
      console.error('Navigation error:', error);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className={`
                    ${isFiddyAdminRoute ? 'bg-black text-white' : 'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground '}
                  `}
              >
                <div
                  className={`
                      flex items-center justify-center rounded-lg aspect-square size-8 
                      ${isFiddyAdminRoute ? 'bg-primary' : 'bg-sidebar-primary text-sidebar-primary-foreground'}
                    `}
                >
                  {isFiddyAdminRoute ? (
                    <activeWorkspace.logo className="size-4" color="white" />
                  ) : (
                    <activeWorkspace.logo className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-sm leading-tight text-left">
                  <span className="font-semibold truncate">
                    {isFiddyAdminRoute ? t('admin.section.title.short') : activeWorkspace.name}
                  </span>
                  <span className="text-xs truncate">
                    {isFiddyAdminRoute ? t('admin.section.status') : activeWorkspace.plan}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="overflow-y-auto max-h-96 rounded-lg min-w-56"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              {isUserSuperAdmin && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t('admin.section.title')}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={handleAdminFiddyNavigation}
                    className="gap-2 justify-between p-2 hover:bg-accent"
                  >
                    <span className="flex items-center">
                      <div className="flex justify-center items-center mr-2 rounded-sm border size-6">
                        {location.pathname.startsWith('/fiddy-admin') ? (
                          <CircleX className="size-4" color="red" />
                        ) : (
                          <ShieldUser className="size-4 text-primary" />
                        )}
                      </div>
                      <span className="font-medium">
                        {location.pathname.startsWith('/fiddy-admin')
                          ? t('admin.workspace.title.back')
                          : t('admin.workspace.title')}
                      </span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1" />
                </>
              )}

              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('workspaces')}
              </DropdownMenuLabel>

              {workspaces.map((workspace) => (
                <DropdownMenuItem
                  key={workspace.value}
                  onClick={() => setActiveWorkspace(workspace)}
                  className="gap-2 justify-between p-2"
                >
                  <span className="flex items-center">
                    <div className="flex justify-center items-center mr-2 rounded-sm border size-6">
                      <workspace.logo className="size-4 shrink-0" />
                    </div>
                    {workspace.name}
                  </span>
                  {workspace.active && <Check className="text-success" />}
                </DropdownMenuItem>
              ))}
              {isUserSuperAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 p-2" onClick={() => setIsDialogOpen(true)}>
                    <div className="flex justify-center items-center rounded-md border size-6 bg-background">
                      <Plus className="size-6 shrink-0" />
                    </div>
                    <div className="font-medium text-muted-foreground">
                      {t('form.workspace.create.title')}
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('form.workspace.create.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form} schema={workspaceFormSchema}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.workspace.name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('form.workspace.name')} {...field} />
                  </FormControl>
                  <FormDescription>{t('form.workspace.name.description')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.workspace.domain')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('form.workspace.domain')} {...field} />
                  </FormControl>
                  <FormDescription>{t('form.workspace.domain.description')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" loading={createWorkspaceMutation.isPending}>
                {t('form.workspace.create.button')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
